import { useState } from "react";
import { Button } from "@/components/ui/button";
import { LandingHeader } from "@/components/landing/landing-header";
import { LandingFooter } from "@/components/landing/landing-footer";
import { AuthModal } from "@/components/landing/auth-modal";
import { ArrowRight, Zap, CheckCircle2 } from "lucide-react";

export default function PhasesPage() {
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalTab, setAuthModalTab] = useState<"login" | "register">("login");

  const openLogin = () => {
    setAuthModalTab("login");
    setAuthModalOpen(true);
  };

  const openRegister = () => {
    setAuthModalTab("register");
    setAuthModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-background">
      <LandingHeader onLoginClick={openLogin} onRegisterClick={openRegister} />

      <section className="pt-24 pb-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Estrutura de Fases</h1>
            <p className="text-xl text-muted-foreground leading-relaxed">
              A progressão na HT CAPITAL é baseada em fases operacionais, projetadas para 
              refletir maturidade técnica e consistência.
            </p>
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto space-y-12">

            <div className="relative">
              <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-purple-500 via-cyan-500 to-amber-500 hidden md:block" />

              <div className="space-y-8">
                <div className="relative pl-0 md:pl-16">
                  <div className="absolute left-0 top-6 hidden md:flex items-center justify-center w-12 h-12 rounded-full bg-purple-500/20 border-2 border-purple-500">
                    <Zap className="h-5 w-5 text-purple-400" />
                  </div>
                  
                  <div className="p-8 rounded-xl bg-gradient-to-br from-purple-500/10 to-purple-500/5 border border-purple-500/30">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="md:hidden p-2 rounded-full bg-purple-500/20">
                        <Zap className="h-5 w-5 text-purple-400" />
                      </div>
                      <div>
                        <h2 className="text-3xl font-bold text-purple-400">VECTOR</h2>
                        <p className="text-sm text-muted-foreground uppercase tracking-wide">Fase de Validação</p>
                      </div>
                    </div>
                    
                    <p className="text-lg text-muted-foreground mb-6">
                      O operador é avaliado quanto à capacidade de sustentar processo, 
                      disciplina e aderência ao plano.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {[
                        "Foco em comportamento e disciplina",
                        "Validação da capacidade de processo",
                        "Avaliação da aderência ao plano",
                        "Métricas comportamentais iniciais"
                      ].map((item, index) => (
                        <div key={index} className="flex items-center gap-2 text-sm">
                          <CheckCircle2 className="h-4 w-4 text-purple-400 flex-shrink-0" />
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="relative pl-0 md:pl-16">
                  <div className="absolute left-0 top-6 hidden md:flex items-center justify-center w-12 h-12 rounded-full bg-cyan-500/20 border-2 border-cyan-500">
                    <Zap className="h-5 w-5 text-cyan-400" />
                  </div>
                  
                  <div className="p-8 rounded-xl bg-gradient-to-br from-cyan-500/10 to-cyan-500/5 border border-cyan-500/30">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="md:hidden p-2 rounded-full bg-cyan-500/20">
                        <Zap className="h-5 w-5 text-cyan-400" />
                      </div>
                      <div>
                        <h2 className="text-3xl font-bold text-cyan-400">AWAKE</h2>
                        <p className="text-sm text-muted-foreground uppercase tracking-wide">Fase de Consolidação</p>
                      </div>
                    </div>
                    
                    <p className="text-lg text-muted-foreground mb-6">
                      O operador demonstra previsibilidade, controle de risco 
                      e estabilidade operacional.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {[
                        "Previsibilidade operacional",
                        "Controle consistente de risco",
                        "Estabilidade comportamental",
                        "Métricas de consolidação"
                      ].map((item, index) => (
                        <div key={index} className="flex items-center gap-2 text-sm">
                          <CheckCircle2 className="h-4 w-4 text-cyan-400 flex-shrink-0" />
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="relative pl-0 md:pl-16">
                  <div className="absolute left-0 top-6 hidden md:flex items-center justify-center w-12 h-12 rounded-full bg-amber-500/20 border-2 border-amber-500">
                    <Zap className="h-5 w-5 text-amber-400" />
                  </div>
                  
                  <div className="p-8 rounded-xl bg-gradient-to-br from-amber-500/10 to-amber-500/5 border border-amber-500/30">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="md:hidden p-2 rounded-full bg-amber-500/20">
                        <Zap className="h-5 w-5 text-amber-400" />
                      </div>
                      <div>
                        <h2 className="text-3xl font-bold text-amber-400">NOIR</h2>
                        <p className="text-sm text-muted-foreground uppercase tracking-wide">Fase Sênior</p>
                      </div>
                    </div>
                    
                    <p className="text-lg text-muted-foreground mb-6">
                      O operador atua sob parâmetros institucionais avançados, com maior 
                      responsabilidade sistêmica e exigência técnica.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {[
                        "Parâmetros institucionais avançados",
                        "Maior responsabilidade sistêmica",
                        "Exigência técnica elevada",
                        "Métricas de excelência"
                      ].map((item, index) => (
                        <div key={index} className="flex items-center gap-2 text-sm">
                          <CheckCircle2 className="h-4 w-4 text-amber-400 flex-shrink-0" />
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 rounded-xl bg-card border border-border text-center">
              <h3 className="text-xl font-bold mb-4">Progressão Baseada em Dados</h3>
              <p className="text-muted-foreground mb-6">
                A progressão entre fases é <strong className="text-primary">automática</strong>, 
                baseada exclusivamente em métricas objetivas processadas pelo sistema.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <div className="px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium">
                  Automática
                </div>
                <div className="px-4 py-2 rounded-full bg-accent/10 text-accent text-sm font-medium">
                  Técnica
                </div>
                <div className="px-4 py-2 rounded-full bg-green-500/10 text-green-500 text-sm font-medium">
                  Auditável
                </div>
              </div>
            </div>

            <div className="text-center py-8">
              <h3 className="text-2xl font-bold mb-4">Pronto para começar?</h3>
              <p className="text-muted-foreground mb-6">
                Solicite sua avaliação e inicie sua jornada na HT CAPITAL.
              </p>
              <Button size="lg" onClick={openRegister} data-testid="button-phases-cta">
                Solicitar Avaliação
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>

          </div>
        </div>
      </section>

      <LandingFooter />

      <AuthModal 
        open={authModalOpen} 
        onOpenChange={setAuthModalOpen}
        defaultTab={authModalTab}
      />
    </div>
  );
}
